import { useCallback, useEffect, useState } from 'react';
import { SxmLead, LeadStatus } from '@/features/sales/types';
import api from '@/utils/api';
import { getErrorMessage } from '@/utils/error';
import { useAutomationUserContext } from './useAutomationUserContext';

export interface CreateSxmLeadDto {
  email: string;
  platform?: string;
  mxmUploadId?: string;
  sourceUrl?: string;
}

export interface UpdateSxmLeadDto {
  email?: string;
  status?: LeadStatus;
}

export const useSxmLeads = (enabled: boolean = false) => {
  const { user, targetUserId, isInternalUser } = useAutomationUserContext();
  const [leads, setLeads] = useState<SxmLead[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchLeads = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    setError(null);
    try {
      const endpoint = isInternalUser && targetUserId !== user?.id ? '/mxm/leads/all' : '/mxm/leads/all';
      const { data } = await api.get(endpoint, { params: { userId: targetUserId } });
      setLeads(data.data || data);
    } catch (err: unknown) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [targetUserId, enabled, isInternalUser, user?.id]);

  const createLead = async (dto: CreateSxmLeadDto): Promise<SxmLead> => {
    try {
      const { data: newLead } = await api.post('/mxm/leads', dto, { params: { userId: targetUserId } });
      if (enabled) {
        await fetchLeads();
      } else {
        setLeads(prev => [...prev, newLead]);
      }
      return newLead.lead || newLead;
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  };

  const updateLead = async (id: string, dto: UpdateSxmLeadDto): Promise<SxmLead> => {
    try {
      const { data: updatedLead } = await api.patch(`/mxm/leads/${id}`, dto, { params: { userId: targetUserId } });
      setLeads(prev => prev.map(lead => lead.id === id ? updatedLead : lead));
      return updatedLead;
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  };

  const bulkUpdateLeads = async (updates: Array<{ id: string; data: UpdateSxmLeadDto }>): Promise<SxmLead[]> => {
    try {
      const { data: updatedLeads } = await api.patch('/mxm/leads/bulk', { updates }, { params: { userId: targetUserId } });
      if (enabled) {
        await fetchLeads();
      } else {
        setLeads((prev: SxmLead[]) => {
          const updatedMap = new Map<string, SxmLead>(updatedLeads.map((lead: SxmLead) => [lead.id, lead]));
          return prev.map((lead: SxmLead) => {
            const updatedLead = updatedMap.get(lead.id);
            return updatedLead ? updatedLead : lead;
          });
        });
      }
      return updatedLeads;
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  };

  const deleteLead = async (id: string): Promise<void> => {
    try {
      await api.delete(`/mxm/leads/${id}`, { params: { userId: targetUserId } });
      setLeads(prev => prev.filter(lead => lead.id !== id));
    } catch (err: unknown) {
      throw new Error(getErrorMessage(err));
    }
  };

  useEffect(() => {
    if (enabled) {
      fetchLeads();
    }
  }, [enabled, fetchLeads]);

  return {
    leads,
    loading,
    error,
    createLead,
    updateLead,
    bulkUpdateLeads,
    deleteLead,
    refetch: fetchLeads,
  };
};
